import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { getPasswordStrengthErrors } from '../utils/passwordValidation';
import { STORAGE_KEYS, SESSION_KEYS, TRANSCRIPTION_PREFERENCE, MEDIA_TYPES } from '../constants';

function useAuth() {
  const { applyTheme } = useTheme();

  // Core auth state
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem(STORAGE_KEYS.TOKEN));

  // Welcome dialog
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  const [isWelcomeBack, setIsWelcomeBack] = useState(false);
  const [inactivityThresholdWeeks, setInactivityThresholdWeeks] = useState(4);
  const [inactivityThresholdActive, setInactivityThresholdActive] = useState(true);

  // Login/Register modal
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginMode, setLoginMode] = useState('login');
  const [credentials, setCredentials] = useState({
    username: '', email: '', phone: '', password: '', passwordConfirm: '',
    website: '',
    mathA: Math.floor(Math.random() * 10) + 1,
    mathB: Math.floor(Math.random() * 10) + 1,
    mathAnswer: ''
  });
  const [smsConsent, setSmsConsent] = useState(false);

  // Verification
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationData, setVerificationData] = useState({
    message: null, username: null,
    hasEmail: false, hasPhone: false, email: null, phone: null
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [showPhoneVerificationNudge, setShowPhoneVerificationNudge] = useState(false);

  // Password management
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  // Forgot/Reset password
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // Preferences
  const [userPreferences, setUserPreferences] = useState({ default_reflection_type: MEDIA_TYPES.VIDEO });

  // Refs
  const initialFetchDone = useRef(false);
  const emailVerificationStartedRef = useRef(false);

  // Compute effective AI settings (backend normalizes booleans, so direct comparison works)
  const effectiveAISettings = useMemo(() => {
    const aiAuthorized = userPreferences.authorize_ai === true;
    const enableFollowups = aiAuthorized && (userPreferences.enable_followups !== false);
    return {
      authorizeAi: aiAuthorized,
      transcriptionPreference: aiAuthorized ? (userPreferences.transcription_preference || TRANSCRIPTION_PREFERENCE.ON_DEMAND) : TRANSCRIPTION_PREFERENCE.NEVER,
      automateTitles: aiAuthorized && (userPreferences.automate_titles === true),
      enableFollowups,
      autoEnhance: aiAuthorized && enableFollowups && (userPreferences.auto_enhance !== false),
      followupDisplayMode: userPreferences.followup_display_mode || 'only_as_threads'
    };
  }, [userPreferences.authorize_ai, userPreferences.transcription_preference, userPreferences.automate_titles, userPreferences.enable_followups, userPreferences.auto_enhance, userPreferences.followup_display_mode]);

  const isAdmin = useCallback(() => {
    return user?.roles?.includes('admin');
  }, [user]);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
  }, []);

  // Legacy helper — still passed as a prop to child components during migration
  const getAxiosConfig = useCallback(() => ({
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  }), [token]);

  // --- Internal helpers ---

  const checkWelcomeDialog = (userData) => {
    if (!userData || userData.hideWelcomeScreen) return;

    if (userData.firstTimeLogin) {
      setIsWelcomeBack(false);
      setShowWelcomeDialog(true);
      api.post('/auth/first-login-complete', {}).catch(err => console.error('Error marking first login complete:', err));
    } else if (inactivityThresholdActive && userData.lastActiveAt) {
      const lastActive = new Date(userData.lastActiveAt);
      const now = new Date();
      const diffMs = now - lastActive;
      const diffWeeks = diffMs / (1000 * 60 * 60 * 24 * 7);

      if (diffWeeks >= inactivityThresholdWeeks) {
        setIsWelcomeBack(true);
        setShowWelcomeDialog(true);
      }
    }
  };

  const resetVerificationState = () => {
    setShowVerificationModal(false);
    setVerificationCode('');
    setResendCooldown(0);
    setVerificationData({
      message: null, username: null,
      hasEmail: false, hasPhone: false, email: null, phone: null
    });
  };

  // --- Fetch functions ---

  const fetchInactivityThreshold = async () => {
    try {
      const response = await api.get('/auth/settings/inactivity-threshold');
      setInactivityThresholdWeeks(response.data.weeks || 4);
      setInactivityThresholdActive(response.data.isActive !== false);
    } catch (error) {
      console.error('Error fetching inactivity threshold:', error);
    }
  };

  const fetchUserPreferences = async () => {
    try {
      const res = await api.get('/preferences');
      setUserPreferences(res.data);
      if (res.data.theme) {
        applyTheme(res.data.theme);
      }
    } catch (error) {
      console.error('Error fetching user preferences:', error);
    }
  };

  const fetchUserInfo = async () => {
    try {
      // First get basic info from token
      const decoded = JSON.parse(atob(token.split('.')[1]));
      console.log('Decoded token:', decoded);

      // Then fetch full user data including headshot
      try {
        const response = await api.get('/auth/me');
        console.log('User data from /auth/me:', response.data);
        const userObj = {
          id: response.data.id || decoded.id,
          username: response.data.username,
          email: response.data.email,
          phone: response.data.phone,
          headshotUrl: response.data.headshotUrl,
          timezone: response.data.timezone,
          smsConsentGiven: response.data.smsConsentGiven,
          smsConsentTimestamp: response.data.smsConsentTimestamp,
          emailNotificationsEnabled: response.data.emailNotificationsEnabled,
          hideWelcomeScreen: response.data.hideWelcomeScreen,
          authMethod: response.data.authMethod,
          emailVerified: response.data.emailVerified,
          phoneVerified: response.data.phoneVerified,
          roles: response.data.roles || []
        };
        console.log('Setting user to:', userObj);
        setUser(userObj);

        checkWelcomeDialog(response.data);
      } catch (fetchError) {
        console.error('Error fetching user from /auth/me:', fetchError);
        // If token is invalid (403) or expired, logout
        if (fetchError.response?.status === 403 || fetchError.response?.status === 401) {
          console.log('Token is invalid or expired, logging out');
          logout();
          return;
        }
        // For other errors, fallback to token data
        const userObj = {
          id: decoded.id,
          username: decoded.username,
          email: decoded.email,
          phone: decoded.phone,
          roles: decoded.roles || []
        };
        console.log('Setting user to (fallback):', userObj);
        setUser(userObj);
      }
    } catch (error) {
      console.error('Error decoding token:', error);
      logout();
    }
  };

  // --- Auth handlers ---

  const handleEmailVerification = async (verificationToken) => {
    try {
      const res = await api.post('/auth/verify-email', {
        token: verificationToken
      });

      // Auto-login with returned token
      if (res.data.token && res.data.user) {
        setToken(res.data.token);
        localStorage.setItem(STORAGE_KEYS.TOKEN, res.data.token);
        setUser(res.data.user);

        // If the phone hasn't been verified yet, give the user one clear chance
        // to do it now, before they move on to onboarding.
        if (res.data.user.firstTimeLogin && res.data.user.phone && !res.data.user.phoneVerified) {
          setVerificationData({
            message: null, username: res.data.user.username,
            hasEmail: false, hasPhone: true,
            email: res.data.user.email, phone: res.data.user.phone
          });
          setShowPhoneVerificationNudge(true);
        }

        checkWelcomeDialog(res.data.user);

        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Email verification failed. The link may be expired or invalid.');

      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();

    // Client-side password strength check for registration
    if (loginMode === 'register') {
      const passwordErrors = getPasswordStrengthErrors(credentials.password);
      if (passwordErrors.length > 0) {
        alert('Password requirements not met:\n' + passwordErrors.join('\n'));
        return;
      }
    }

    try {
      const endpoint = loginMode === 'login' ? '/auth/login' : '/auth/register';
      const data = loginMode === 'login'
        ? { username: credentials.username, password: credentials.password }
        : {
            ...credentials,
            smsConsent: credentials.phone ? smsConsent : false,
            smsConsentTimestamp: credentials.phone && smsConsent ? new Date().toISOString() : null
          };

      const res = await api.post(endpoint, data);

      // Check if response requires verification (for registration)
      if (res.data.requiresVerification) {
        setVerificationData({
          message: res.data.message,
          username: res.data.username,
          hasEmail: res.data.hasEmail,
          hasPhone: res.data.hasPhone,
          email: res.data.email,
          phone: res.data.phone
        });
        setShowVerificationModal(true);
        return;
      }

      // Otherwise, login successful (has token)
      const authToken = res.data.token;
      setToken(authToken);
      localStorage.setItem(STORAGE_KEYS.TOKEN, authToken);
      setUser(res.data.user);
      setShowLoginModal(false); // Close login modal on successful login

      checkWelcomeDialog(res.data.user);

      // Check if there's a pending invitation to accept
      const invitationToken = sessionStorage.getItem(SESSION_KEYS.INVITATION_TOKEN);
      if (invitationToken) {
        try {
          await api.post(`/sharing/accept-token/${invitationToken}`, {});
          sessionStorage.removeItem(SESSION_KEYS.INVITATION_TOKEN);
          sessionStorage.removeItem(SESSION_KEYS.INVITATION_EMAIL);
          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          alert('Welcome! You now have access to shared reflections.');
        } catch (invError) {
          console.error('Error accepting invitation:', invError);
          // Don't block login if invitation acceptance fails
          alert('You have been logged in, but there was an issue accepting the invitation. Please check the Settings page.');
        }
      }
    } catch (error) {
      // Check if error is due to unverified account (for login)
      if (error.response?.status === 403 && error.response?.data?.requiresVerification) {
        const data = error.response.data;
        setVerificationData({
          message: data.message,
          username: data.username,
          hasEmail: data.hasEmail,
          hasPhone: data.hasPhone,
          email: data.email,
          phone: data.phone
        });
        setShowVerificationModal(true);
      } else {
        alert(error.response?.data?.error || 'Authentication failed');
      }
    }
  };

  // Only ever called from the SMS code entry form, which is only shown when a phone was provided
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/auth/verify-sms', {
        phone: verificationData.phone,
        code: verificationCode
      });

      // Auto-login with returned token
      if (res.data.token && res.data.user) {
        const authToken = res.data.token;
        setToken(authToken);
        localStorage.setItem(STORAGE_KEYS.TOKEN, authToken);
        setUser(res.data.user);

        checkWelcomeDialog(res.data.user);

        // Close modals and reset state
        setShowLoginModal(false);
        setShowPhoneVerificationNudge(false);
        resetVerificationState();
      } else {
        resetVerificationState();
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Verification failed');
    }
  };

  const dismissPhoneVerificationNudge = () => {
    setShowPhoneVerificationNudge(false);
    resetVerificationState();
  };

  const handleResendVerification = async (method) => {
    if (resendCooldown > 0 || resending) return;

    setResending(true);
    try {
      const res = await api.post('/auth/resend-verification-code', {
        username: verificationData.username,
        method
      });

      setResendCooldown(30);

      if (res.data.message) {
        setVerificationData(prev => ({ ...prev, message: res.data.message }));
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to resend verification code');
    } finally {
      setResending(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    try {
      await api.put('/users/password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      alert('Password changed successfully');
      setShowPasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to change password');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      setResetMessage('If that email exists, a reset link has been sent. Please check your inbox.');
      setForgotEmail('');
    } catch (error) {
      setResetMessage('An error occurred. Please try again.');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');

    if (newPassword !== confirmNewPassword) {
      setResetError('Passwords do not match');
      return;
    }

    const passwordErrors = getPasswordStrengthErrors(newPassword);
    if (passwordErrors.length > 0) {
      setResetError('Password requirements not met: ' + passwordErrors.join(', '));
      return;
    }

    try {
      await api.post('/auth/reset-password', {
        token: resetToken,
        newPassword: newPassword
      });
      setResetSuccess(true);
      setTimeout(() => {
        setShowResetPassword(false);
        window.history.pushState({}, '', window.location.pathname);
      }, 3000);
    } catch (error) {
      setResetError(error.response?.data?.error || 'Failed to reset password');
    }
  };

  // --- Effects ---

  // Initial fetch on token
  useEffect(() => {
    if (token && !initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchUserInfo();
      fetchUserPreferences();
      fetchInactivityThreshold();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Activity tracking with 5-minute granularity
  useEffect(() => {
    if (!token) return;

    const FIVE_MINUTES = 5 * 60 * 1000;

    const sendActivityPing = async () => {
      const lastPing = localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY_PING);
      const now = Date.now();

      if (lastPing && (now - parseInt(lastPing, 10)) < FIVE_MINUTES) {
        return;
      }

      try {
        await api.post('/auth/activity', {});
        localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY_PING, now.toString());
      } catch (error) {
        console.error('Error recording activity:', error);
      }
    };

    sendActivityPing();
    const intervalId = setInterval(sendActivityPing, 60 * 1000);

    return () => clearInterval(intervalId);
  }, [token]);

  // Handle URL params (email verification, password reset, invitation)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    const invitationToken = params.get('invitation');
    const invitationEmail = params.get('email');
    const pathname = window.location.pathname;

    // Handle email verification
    // Guarded against React StrictMode's dev-mode double-invocation of effects —
    // the token is single-use server-side, so a second call would fail as "already used".
    if (pathname === '/verify-email' && urlToken && !emailVerificationStartedRef.current) {
      emailVerificationStartedRef.current = true;
      handleEmailVerification(urlToken);
    }
    // Handle password reset
    else if (urlToken && !pathname.includes('verify-email')) {
      setResetToken(urlToken);
      setShowResetPassword(true);
    }

    if (invitationToken) {
      // Store invitation token to use after signup/login
      sessionStorage.setItem(SESSION_KEYS.INVITATION_TOKEN, invitationToken);
      if (invitationEmail) {
        sessionStorage.setItem(SESSION_KEYS.INVITATION_EMAIL, invitationEmail);
        // Pre-fill email in signup form
        setCredentials(prev => ({ ...prev, email: decodeURIComponent(invitationEmail) }));
      }
      setLoginMode('signup'); // Switch to signup mode
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  return {
    // Core
    user, setUser, token, isAdmin, logout, getAxiosConfig,
    // Preferences
    userPreferences, setUserPreferences, effectiveAISettings,
    // Login modal
    showLoginModal, setShowLoginModal, loginMode, setLoginMode,
    credentials, setCredentials, smsConsent, setSmsConsent,
    handleAuth,
    // Verification
    showVerificationModal, setShowVerificationModal,
    verificationData, verificationCode, setVerificationCode,
    resendCooldown, resending,
    handleVerifyCode, handleResendVerification,
    showPhoneVerificationNudge, dismissPhoneVerificationNudge,
    // Password
    showPasswordModal, setShowPasswordModal,
    passwordData, setPasswordData,
    handlePasswordChange,
    // Forgot/Reset
    showForgotPassword, setShowForgotPassword,
    forgotEmail, setForgotEmail,
    resetMessage, setResetMessage,
    handleForgotPassword,
    showResetPassword,
    newPassword, setNewPassword,
    confirmNewPassword, setConfirmNewPassword,
    resetError, resetSuccess,
    handleResetPassword,
    // Welcome
    showWelcomeDialog, setShowWelcomeDialog, isWelcomeBack,
  };
}

export default useAuth;
